import { Alert, Button, Empty, FormInstance, Modal, notification, Table, Tooltip } from 'antd';
import { useContext, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { ProjectDataContext } from '../contexts/ProjectDataContext';
import {
  AggregatedScore,
  CategoryWeightResponse,
  KPIWeightResponse,
  MonthlyData,
  TeamActualModel,
} from '../types/metrics';
import { ABOVE_TARGET, BELOW_TARGET, DATE_ID_FORMAT } from '../common/constants';
import { ColumnsType } from 'antd/lib/table';
import './MonthlyData.scss';
import { isNumber, orderBy } from 'lodash';
import { MonthlyDataToolbar } from './EngagementMonthlyData.styled';
import { useFetch } from '../hooks/useFetch';
import { fetchKPIByteam } from '../apis/customMetric';
import { TeamWeightMonthlyTable } from './TeamWeightMonthlyTable';
import { fetchCategoryByteam } from '../apis/fetchCategory';
import { CloseOutlined } from '@ant-design/icons';
import { updateActual } from '../apis/measureClient';
import { TeamScoreUpdateTable } from './TeamScoreUpdateTable';
import { AuthContext } from '../contexts/AuthContext';

const DATE_COL_WIDTH = 75;
const LABEL_COL_WIDTH = 200;

// FEAT: Edit actual score directly for the team
const ENABLE_EDIT_ACTUAL = true;

const getCellAttributes = (record: AggregatedScore, dateId: number): any => {
  const score = record[dateId]?.score;
  if (score == null) {
    return { 'data-target': 'null' };
  }

  if (score < BELOW_TARGET) {
    return { 'data-target': 'below' };
  }

  if (score < ABOVE_TARGET) {
    return { 'data-target': 'on' };
  }

  return { 'data-target': 'above' };
};

const getRowSpans = (arr, key) => {
  let sameValueLength = 0;
  const rowSpans = [];
  for (let i = arr.length - 1; i >= 0; i--) {
    if (i === 0) {
      rowSpans[i] = sameValueLength + 1;
      continue;
    }
    if (arr[i][key] === arr[i - 1][key]) {
      rowSpans[i] = 0;
      sameValueLength++;
    } else {
      rowSpans[i] = sameValueLength + 1;
      sameValueLength = 0;
    }
  }
  return rowSpans;
};

const createMonthlyColumns = (
  dates: number[],
  targetDates: number[],
  rowSpans: any[],
  isAdmin: boolean = false,
  hoverTitle: number = 0,
  onTitleClick: (data: number) => void,
  setHoverTitle: (data: number) => void
) => {
  const dateCols: ColumnsType = [
    {
      title: 'Category',
      dataIndex: 'theme',
      width: LABEL_COL_WIDTH,
      key: 'theme',
      fixed: 'left',
      render: (value, record: MonthlyData, index) => {
        const obj = {
          children: record.theme,
          props: { rowSpan: rowSpans[index] },
        };
        return obj;
      },
      onCell: (record: MonthlyData) => ({
        style: {
          backgroundColor: record.color,
        },
      }),
    },
    {
      dataIndex: 'label',
      title: 'KPI',
      fixed: 'left',
      width: LABEL_COL_WIDTH,
      className: 'monthly-data-name-column',
      render: (value, record: MonthlyData, index) => record.type,
      onCell: (record: MonthlyData) => ({
        style: {
          backgroundColor: record.color,
        },
      }),
    },
  ];

  const sortedDates = orderBy(dates, (a) => a, 'desc');
  const sortedTargets = orderBy(targetDates, (a) => a, 'desc');

  let currentTarget = sortedTargets.shift();
  let targetDateId = currentTarget ?? sortedDates[sortedDates.length - 1];

  sortedDates.forEach((dateId) => {
    const date = moment(`${dateId}`, DATE_ID_FORMAT);
    // Start new target date ranges
    if (targetDateId > dateId) {
      currentTarget = sortedTargets.shift();
      targetDateId = currentTarget;
    }

    // Only add target column at start of month ranges
    if (currentTarget) {
      dateCols.push({
        title: 'Target',
        dataIndex: [currentTarget, 'target'],
        width: DATE_COL_WIDTH,
        align: 'center',
        className: 'monthly-data-column target',
        render: (value, record, index) => value?.toFixed(2),
      });
      currentTarget = null;
    }

    dateCols.push({
      title: date.format('MMM-YY'),
      ...(ENABLE_EDIT_ACTUAL
        ? {
            title: (
              <Tooltip title="Click to update actual scores" visible={isAdmin && hoverTitle === dateId}>
                {date.format('MMM-YY')}
              </Tooltip>
            ),
            onHeaderCell: () => ({
              onClick: () => {
                isAdmin && onTitleClick(dateId);
              },
              onMouseEnter: () => setHoverTitle(dateId),
              onMouseLeave: () => setHoverTitle(0),
              style: { cursor: `${isAdmin ? 'pointer' : 'auto'}` },
            }),
          }
        : undefined),
      children: [
        {
          dataIndex: [dateId, 'actual'],
          title: 'Actual',
          width: DATE_COL_WIDTH,
          align: 'center',
          className: 'monthly-data-column actual',
          onCell: (record: AggregatedScore) => getCellAttributes(record, dateId),
          render: (val: number) => val?.toFixed(2),
        },
        {
          dataIndex: [dateId, 'score'],
          title: 'Score',
          width: DATE_COL_WIDTH,
          align: 'center',
          className: 'monthly-data-column score',
          onCell: (record: AggregatedScore) => getCellAttributes(record, dateId),
          render: (val: number) => val?.toFixed(2),
        },
      ],
    });
  });

  return dateCols;
};

interface IProps {
  projectId: number;
  dateId: number;
  teamName: string;
  refetchData?: () => void;
}

export const MonthlyTable = ({ projectId, dateId, teamName, refetchData }: IProps) => {
  const {
    user: { isAdmin },
  } = useContext(AuthContext);
  const { monthlyData, dates, targetDates } = useContext(ProjectDataContext);
  const [kpiWeight, setKpiWeight] = useState<KPIWeightResponse[]>([]);
  const [categoryWeight, setCategoryWeight] = useState<CategoryWeightResponse[]>([]);
  const [hoverTitle, setHoverTitle] = useState<number>(0);
  const formRef = useRef<FormInstance>(null);
  useEffect(() => {
    (async function () {
      try {
        const kpiData = await fetchKPIByteam(dateId, projectId, false);
        setKpiWeight(kpiData);
      } catch (error) {
        console.log(error);
      }
    })();
  }, [projectId, dateId]);

  useEffect(() => {
    (async function () {
      try {
        const catogryData = await fetchCategoryByteam(kpiWeight[0]?.dateId, projectId);
        setCategoryWeight(catogryData);
      } catch (error) {
        console.log(error);
      }
    })();
  }, [kpiWeight]);

  const updateTeamActual = async (data: any, kpiWeight: KPIWeightResponse[]) => {
    try {
      var actualList: TeamActualModel[] = [
        ...kpiWeight.map((value, index) => {
          var actual: TeamActualModel = {
            teamName: teamName,
            kpiName: value.name,
            categoryName: value.categoryName,
            actualScore: data[value.name],
          };
          return actual;
        }),
      ];
      await updateActual(projectId, data.dateId, actualList);
      refetchData();
    } catch (error) {
      throw error;
    }
  };

  const onTitleClick = async (selectedDateId: number) => {
    try {
      let kpiData = await fetchKPIByteam(selectedDateId, projectId, false);
      if (kpiData.length === 0) {
        return;
      }

      const catogryData = await fetchCategoryByteam(kpiData[0]?.dateId, projectId);
      if (catogryData.length === 0) {
        return;
      }

      var inputData = {};
      inputData['dateId'] = selectedDateId;
      kpiData = kpiData.filter((value) => {
        var dataField = monthlyData.find((x) => x.type === value.name);
        if (dataField && dataField[selectedDateId] && isNumber(dataField[selectedDateId].score)) {
          inputData[value.name] = dataField[selectedDateId].actual;
          return true;
        }
        return false;
      });

      if (Object.keys(inputData).length === 1) {
        return;
      }

      Modal.info({
        // eslint-disable-next-line react/display-name
        title: (
          <>
            <span>
              {teamName} - {selectedDateId}
            </span>
            <Button type="link" onClick={() => Modal.destroyAll()} style={{ float: 'right' }}>
              <CloseOutlined />
            </Button>
          </>
        ),
        width: '60%',
        content: (
          <TeamScoreUpdateTable
            data={inputData}
            kpiWeight={kpiData}
            categoryWeight={catogryData}
            editableColName="Actual"
            ref={formRef}
          />
        ),
        onOk: async () => {
          const formInstance = formRef.current;
          if (!formInstance) {
            console.error('Form instance not exist!');
            return;
          }
          try {
            const values = await formInstance.validateFields();
            inputData = { ...inputData, ...values };
            await updateTeamActual(inputData, kpiData);
          } catch (error) {
            error.status &&
              notification.error({
                message: `Error: ${error.status}`,
                description: error?.data?.error?.message,
                duration: 3,
              });

            error?.data?.error?.validationErrors &&
              error?.data?.error?.validationErrors.forEach((x: any) => {
                notification.error({
                  message: `Validation Error`,
                  description: x.message,
                  duration: 3,
                });
              });

            error?.errorFields &&
              error?.errorFields.forEach((x: any) => {
                notification.error({
                  message: `${x.name} Input Error`,
                  description: x.errors[0],
                  duration: 3,
                });
              });

            console.error(error);
            throw error;
          }
        },
        okText: 'Save',
      });
    } catch (error) {
      return;
    }
  };

  const showModal = () => {
    Modal.info({
      // eslint-disable-next-line react/display-name
      title: (
        <>
          <span>Team weight configuration - {dateId}</span>
          <Button type="link" onClick={() => Modal.destroyAll()} style={{ float: 'right' }}>
            <CloseOutlined />
          </Button>
        </>
      ),
      width: '60%',
      content: <TeamWeightMonthlyTable kpiWeight={kpiWeight} categoryWeight={categoryWeight} dateId={dateId} />,
      onOk() {},
      okText: 'Close',
    });
  };
  if (dates.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const rowSpans = getRowSpans(monthlyData, 'theme');
  const cols = createMonthlyColumns(dates, targetDates, rowSpans, isAdmin, hoverTitle, onTitleClick, setHoverTitle);
  const scrollX = `calc(${LABEL_COL_WIDTH}px + 50%)`;

  const handleRenderRow = (monthlyData: MonthlyData[], kpiWeight: KPIWeightResponse[]) => {
    let arr = [];
    kpiWeight.forEach((item) => {
      let temp = monthlyData.findIndex((m) => item.name.trim() === m.type.trim());
      if (temp === -1) {
        arr.push(item);
      }
    });
    var result = arr.map((item) => ({
      type: item?.name,
      label: `${item?.name}${item?.categoryName}`,
      theme: item?.categoryName,
      color: item?.color,
    }));
    return monthlyData.concat(result).sort((a, b) => (a.theme.toLowerCase() > b.theme.toLowerCase() ? 1 : -1));
  };
  return (
    <div>
      <MonthlyDataToolbar>
        <Button type="link" onClick={showModal} className="btn-view-config" style={{ paddingLeft: 0, paddingTop: 0 }}>
          View team configuration
        </Button>
      </MonthlyDataToolbar>
      <Table
        className="project-monthly-data-table"
        columns={cols}
        bordered
        rowKey="label"
        dataSource={handleRenderRow(monthlyData, kpiWeight)}
        scroll={{ x: scrollX }}
        pagination={false}
      />
    </div>
  );
};
